<template>
  <v-row>
    <v-col cols="12" class="pa-6">
      <v-timeline
        density="compact"
        truncate-line="both"
        class="mb-0"
        side="end"
        line-color="background-medium"
      >
        <v-timeline-item
          v-for="(article, index) in articleTypes"
          :key="index"
          :dot-color="
            selections.some((selection) => selection.name === article.value)
              ? 'primary'
              : 'background-medium'
          "
          :rules="mandatory"
          size="small"
        >
          <h4>{{ article.term }}</h4>
          <v-radio-group v-model="selectionStore[index]" class="mt-0" inline :rules="mandatory">
            <div
              v-for="(option, articlesKey) in getArticlesGroupedByName(article.term)"
              :key="articlesKey"
              class="d-flex align-center"
            >
              <v-radio
                :label="option.value"
                :value="option"
                :disabled="option.disabled"
                @change="selectionChange(article, option)"
              />
              <tooltip
                v-if="option.disabled && option.disabledReason"
                :message="option.disabledReason"
              />
            </div>
          </v-radio-group>
          <template
            v-if="
              article.subArticles &&
                selections.some(
                  (selection) =>
                    selection.name === article.value && selection.article.value !== 'Keine Auswahl',
                )
            "
          >
            <section
              v-for="(subOption, subIndex) in article.subArticles"
              :key="`sub_label_${subIndex}`"
            >
              <h4>{{ subOption.term }}</h4>
              <v-radio-group
                v-if="subOption.type === 'radio'"
                :key="`sub_${subIndex}`"
                class="mt-0"
                inline
              >
                <v-radio
                  v-for="(subArticle, radioIndex) in getArticlesGroupedByName(subOption.term)"
                  :key="`sub_radio_${radioIndex}`"
                  :label="subArticle.value"
                  :value="subArticle"
                  @change="selectionChange(subOption, subArticle, true)"
                />
              </v-radio-group>
              <v-checkbox
                v-for="(subArticle, subArticlesKey) in getArticlesGroupedByName(subOption.term)"
                v-else-if="subOption.type === 'checkbox'"
                :key="subArticlesKey"
                class="inline-checkbox mt-0"
                :value="subArticle"
                hide-details
                dense
                @change="selectionCheckBoxChange(subOption.value, subArticle)"
              >
                <template #label>
                  <label class="body-2" v-text="subArticle.value" />
                </template>
              </v-checkbox>
            </section>
          </template>
        </v-timeline-item>
      </v-timeline>
      <v-progress-linear v-if="articleList.length === 0" color="primary" />
    </v-col>
  </v-row>
</template>
<script setup lang="ts">
import { ref, reactive } from 'vue';
import type { Article, SelectedArticle, CartConfigurationInput } from '../../types/common';
import { mandatory } from '../../composables/validations';
import { getCartArticles } from '../../services/api';
import Tooltip from '../common/custom-tooltip.vue';

interface Props {
  articles: Article[];
  selectionsProp: SelectedArticle[];
}

interface ArticleType {
  term: string;
  value: string;
  type?: string;
  subArticles?: ArticleType[];
}

const { articles, selectionsProp } = defineProps<Props>();

const articleList = ref(articles);

const emit = defineEmits(['update-configuration']);

const serviceFee = articles.find(({ term }) => term === 'Bereitstellungspreis');

let selections = reactive<SelectedArticle[]>(
  selectionsProp ?? [
    {
      name: 'serviceFee',
      article: serviceFee,
    },
  ],
);
const selectionStore = ref<Article[]>([]);
const articleTypes = reactive<ArticleType[]>([
  { term: 'Produkt', value: 'selectedProduct' },
  { term: 'Festnetzoption', value: 'selectedLandlineOption' },
  { term: 'Laufzeit', value: 'selectedPeriod' },
  { term: 'Hardware', value: 'selectedHardware' },
  { term: 'Sicherheitspaket', value: 'selectedSecurityPackage' },
  { term: 'Auslandsoption', value: 'selectedAbroadOption' },
  {
    term: 'ZuhauseTV',
    value: 'selectedTVOption',
    subArticles: [
      { term: 'Basis Receiver', value: 'basicReceiver', type: 'radio' },
      { term: 'Zusatz Receiver', value: 'additionalReceiver', type: 'checkbox' },
      { term: 'Aufnahmespeicher', value: 'recordingMemory', type: 'radio' },
      { term: 'Themenpakete', value: 'themedPackages', type: 'checkbox' },
      { term: 'Sprachpakete', value: 'languagePackages', type: 'checkbox' },
    ],
  },
  { term: 'Rechnung', value: 'invoiceDelivery' },
]);

const getArticles = async () => {
  const param: CartConfigurationInput = {};
  selections.forEach((selection: SelectedArticle) => {
    param[selection.name as keyof CartConfigurationInput] = selection.article.value;
  });

  const [articlesError, articleData] = await getCartArticles(param);
  if (!articlesError) {
    articleList.value = articleData;
  }
};

function getArticlesGroupedByName(articleName: string): Article[] {
  return articleList.value.filter(({ term }) => term === articleName);
}

function selectionChange(
  type: ArticleType,
  selectedArticle: Article,
  isSubArticle: boolean = false,
) {
  const selection = selections.find((element: SelectedArticle) => element.name === type.value);
  if (selection) {
    selection.article = selectedArticle;
    if (type.subArticles?.length) {
      selections = selections.filter(
        (element: SelectedArticle) => type.subArticles!
          .map(({ value }) => value)
          .every(value => value !== element.name),
      );
    }
  } else {
    selections.push({ name: type.value, article: selectedArticle });
  }

  const selectionIndex = selections.findIndex(({ name }) => name === type.value);
  const storeIndex = selectionStore.value.findIndex(({ term }) => term === selectedArticle.term);
  if (selectionIndex > -1 && selectionIndex + 1 < selections.length && !isSubArticle) {
    selections.splice(selectionIndex + 1);
  }
  if (storeIndex > -1 && storeIndex + 1 < selectionStore.value.length && !isSubArticle) {
    selectionStore.value.splice(storeIndex + 1);
  }

  // Shipping costs apply to every router (hardware with a monthly or one-time
  // price), i.e. both instalment and one-time purchase. A customer's own device
  // (no price) does not incur shipping costs.
  if (
    selectedArticle.term === 'Hardware'
    && (!!selectedArticle.monthlyPrice || !!selectedArticle.onetimePrice)
  ) {
    const shippingCosts = articles.find(({ term }) => term === 'Versandkosten');
    if (shippingCosts) {
      selections.push({ name: type.value, article: shippingCosts });
    }
  }

  getArticles();
  emit('update-configuration', selections);
}
const selectionCheckBoxChange = (name: string, article: Article) => {
  const selectionIndex = selections.findIndex(
    (element: SelectedArticle) => element.name === name && element.article.value === article.value,
  );
  if (selectionIndex !== -1) {
    selections.splice(selectionIndex, 1);
  } else {
    selections.push({ name, article });
  }
  emit('update-configuration', selections);
};
</script>
<style lang="scss">
  .v-timeline--vertical.v-timeline {
    grid-row-gap: 0px !important;
  }

  .inline-checkbox.inline-checkbox {
    display: inline-block;
  }
</style>
